import { Formik, FormikProps, FormikConfig } from 'formik';
import { isFunction } from 'lodash-es';
import React, { useMemo } from 'react';

import * as errors from 'utils/errors';

import Wizard from './Wizard';
import { WizardProvider, initialState, WizardStepContextMap, useWizardContext } from './context';
import {
  WizardFormConfig,
  WizardProps,
  ComponentProps,
  WizardStepsComponentsMap,
  RenderProps
} from './interfaces';
import * as Styled from './styled';

/**
 * Code examples:
 * https://github.com/mjangir/formik-wizard-form
 * https://github.com/jcmcneal/react-step-wizard
 */

/**
 * Formik element wrapper component
 * @param props
 */
function FormikWrapper<T>(
  props: Omit<WizardFormConfig<T>, 'onSubmit'> & RenderProps<T> & Pick<FormikConfig<T>, 'onSubmit'>
) {
  /**
   * Pass the validationSchema property to Formik internal validationSchema prop by check whether passing attribute was a function or the Yup schema object itself
   */
  const { appStates } = useWizardContext();
  const { renderStep, validationSchema, ...rest } = props;
  const schema = isFunction(validationSchema) ? validationSchema(appStates) : validationSchema;

  return (
    <Formik validationSchema={schema} {...rest}>
      {(formProps: FormikProps<T>) => (
        <Styled.FormWrapper>{renderStep(formProps)}</Styled.FormWrapper>
      )}
    </Formik>
  );
}

function Wrapper<T>({
  initialErrors,
  initialTouched,
  initialValues,
  onSubmit = () => {},
  onReset,
  initialAppStates = {},
  enableReinitialize,
  steps,
  breadCrumbs,
  opacityBetweenLoading,
  initialStepId,
  isHashEnabled,
  isStepperEnabed,
  showHeader,
  showFooter,
  storage,
  validationSchema,
  onWindowClose,
  headerProps,
  scrollOnSubmitError
}: ComponentProps<T>) {
  /**
   * Calcuate the steps collection as object with "key" as step id, and "value" as the step object
   * Returns [stepIds, stepsComponents] array with "stepsContextData" -> step information (without the lazy `Component` prop) and "stepsComponents" => step information of the lazy "Component" prop specific (avoiding the issue where the context provider cannot persist lazy component)
   */
  const [stepsContextData, stepsComponents] = useMemo(() => {
    const data: WizardStepContextMap<T> = {};
    const componentData: WizardStepsComponentsMap<T> = {};
    steps.map(step => {
      // validate the step "id" prop is unique across all steps
      if (data[step.id]) {
        throw new errors.SVError(`A given step with id ${step.id} has already been declared`);
      }
      componentData[step.id] = step.Component;
      data[step.id] = {
        id: step.id,
        title: step.title,
        navigation: step.navigation,
        stepName: step.stepName,
        validation: step.validation
      };
    });
    return [data, componentData];
  }, [steps]);

  // validate the provided initialStepId prop is valid and exist in the array of steps
  if (!initialStepId || !stepsContextData[initialStepId]) {
    throw new errors.SVError(
      'initialStep prop was not provided or was not found in the array of steps'
    );
  }
  const missingStepNameIndex = useMemo(() => steps.findIndex(s => !s.stepName), [steps]);

  // Validate the stepName prop exists across all steps when `isStepperEnabled` was set to true
  if (isStepperEnabed && missingStepNameIndex !== -1) {
    throw new errors.SVError(
      `isStepperEnabled prop was set to "true" however step '${
        missingStepNameIndex + 1
      }' in the array is missing its stepName property`
    );
  }

  // Construct the <Formik /> main props
  const formProps: Omit<WizardFormConfig<T>, 'onSubmit'> = {
    initialValues,
    initialErrors,
    initialTouched,
    enableReinitialize,
    validationSchema,
    onReset
  };
  // Construct the <Wizard /> props
  const wizardProps: Omit<WizardProps<T>, 'children'> = {
    isHashEnabled,
    isStepperEnabed,
    breadCrumbs,
    stepsComponents,
    showHeader,
    showFooter,
    storage,
    opacityBetweenLoading,
    onWindowClose,
    headerProps,
    scrollOnSubmitError
  };

  return (
    <WizardProvider
      {...initialState(formProps)}
      appStates={initialAppStates}
      steps={stepsContextData}
      currentStepId={initialStepId}>
      <Wizard {...wizardProps}>
        {({ renderStep }) => {
          return <FormikWrapper {...formProps} onSubmit={onSubmit} renderStep={renderStep} />;
        }}
      </Wizard>
    </WizardProvider>
  );
}

export default Wrapper;
